import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, TextField, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { MaterialReactTable } from 'material-react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import intercomService from '../../services/intercom.service';
import styles from './Intercom.module.css';

const DEFAULT_PAGE_SIZE = 10;
const STATUS_OPTIONS = [
	{ value: 'all', label: 'All' },
	{ value: 'open', label: 'Open' },
	{ value: 'closed', label: 'Closed' },
	{ value: 'snoozed', label: 'Snoozed' },
];

const getTicketId = (ticket) => {
	if (!ticket) {
		return null;
	}
	return ticket.id || ticket.ticket_id || ticket.ticketId || null;
};

const formatTimestamp = (timestamp) => {
	if (!timestamp) {
		return '';
	}
	const date = new Date(timestamp * 1000);
	return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

const IntercomConversations = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [filters, setFilters] = useState(() => ({
		email: searchParams.get('email') || '',
		status: searchParams.get('status') || 'all',
		conversationId: searchParams.get('conversationId') || '',
	}));
	const [appliedFilters, setAppliedFilters] = useState(() => ({
		email: searchParams.get('email') || '',
		status: searchParams.get('status') || 'all',
		conversationId: searchParams.get('conversationId') || '',
	}));
	const [page, setPage] = useState(() => {
		const pageParam = Number(searchParams.get('page') || 1);
		return Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;
	});
	const [conversations, setConversations] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const startingAfterByPage = useRef({});

	useEffect(() => {
		document.title = 'Intercom Conversations - GSD Support Admin';
		return () => {
			document.title = 'GSD Support Admin';
		};
	}, []);

	const loadConversations = useCallback(async () => {
		setIsLoading(true);
		setErrorMessage('');
		try {
			const startingAfter = page > 1 ? startingAfterByPage.current[page] : '';
			const data = await intercomService.searchConversations({
				page,
				perPage: DEFAULT_PAGE_SIZE,
				email: appliedFilters.email,
				status: appliedFilters.status,
				conversationId: appliedFilters.conversationId,
				startingAfter,
			});
			const result = data?.result || {};
			setConversations(result.conversations || []);
			setTotalCount(result.total_count || 0);
			const nextStartingAfter = result?.pages?.next?.starting_after || result?.starting_after || '';
			if (nextStartingAfter) {
				startingAfterByPage.current[page + 1] = nextStartingAfter;
			} else {
				delete startingAfterByPage.current[page + 1];
			}
		} catch (error) {
			setErrorMessage(error?.message || 'Failed to load conversations.');
		} finally {
			setIsLoading(false);
		}
	}, [appliedFilters, page]);

	useEffect(() => {
		loadConversations();
	}, [loadConversations]);

	const handleSearch = () => {
		const nextFilters = {
			email: filters.email.trim(),
			status: filters.status,
			conversationId: filters.conversationId.trim(),
		};
		startingAfterByPage.current = {};
		setAppliedFilters(nextFilters);
		setPage(1);
		setSearchParams({
			...nextFilters,
			page: '1',
		});
	};

	const handleReset = () => {
		const cleared = { email: '', status: 'all', conversationId: '' };
		startingAfterByPage.current = {};
		setFilters(cleared);
		setAppliedFilters(cleared);
		setPage(1);
		setSearchParams({
			...cleared,
			page: '1',
		});
	};

	const columns = useMemo(
		() => [
			{
				header: 'View',
				id: 'type',
				Cell: ({ row }) => {
					const ticketId = getTicketId(row.original.ticket);
					const hasTicket = Boolean(ticketId);
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							{hasTicket ? (
								<Tooltip title="View conversation + ticket details">
									<Button
										variant="text"
										size="small"
										onClick={() => navigate(`/intercom/conversations/${row.original.id}`)}
									>
										<ChatBubbleOutlineIcon fontSize="small" />
										<ConfirmationNumberIcon fontSize="small" />
									</Button>
								</Tooltip>
							) : (
								<Tooltip title="Click to see the conversation">
									<Button
										variant="text"
										size="small"
										onClick={() => navigate(`/intercom/conversations/${row.original.id}`)}
									>
										<ChatBubbleOutlineIcon fontSize="small" />
									</Button>
								</Tooltip>
							)}
						</Box>
					);
				},
			},

			{
				header: 'Email',
				accessorFn: (row) => row?.source?.author?.email || '',
				id: 'email',
			},
			{
				header: 'Status',
				accessorFn: (row) => row?.state || '',
				id: 'status',
			},
			{
				header: 'Title',
				accessorFn: (row) => row?.title || row?.source?.subject || '',
				id: 'title',
			},

			{
				header: 'Created',
				accessorFn: (row) => formatTimestamp(row?.created_at),
				id: 'createdAt',
			},
			{
				header: 'Updated',
				accessorFn: (row) => formatTimestamp(row?.updated_at),
				id: 'updatedAt',
			},
			{
				header: 'Source',
				accessorFn: (row) => row?.source?.type || '',
				id: 'source',
			},
			{
				header: 'Conversation ID',
				accessorKey: 'id',
			},
		],
		[navigate]
	);

	return (
		<div className={styles.divStyle}>
			<div className={styles.sectionContainer}>
				<div className={styles.sectionHeader}>
					<Tooltip title="Intercom">
						<button
							type="button"
							className={styles.backButton}
							onClick={() => navigate('/intercom')}
						>
							<ArrowBackIcon className={styles.backIcon} />
						</button>
					</Tooltip>
					<div>
						<h1 className={styles.sectionTitle}>Intercom Conversations</h1>
						<p className={styles.sectionDescription}>
							Search conversations by email, status, or conversation ID.
						</p>
					</div>
				</div>
			</div>

			<Paper className={styles.tableContainer}>
				<Box className={styles.filtersRow}>
					<TextField
						label="Email"
						variant="outlined"
						size="small"
						value={filters.email}
						onChange={(event) => setFilters((prev) => ({ ...prev, email: event.target.value }))}
						className={styles.filterField}
					/>
					<FormControl size="small" className={styles.filterField}>
						<InputLabel>Status</InputLabel>
						<Select
							label="Status"
							value={filters.status}
							onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
						>
							{STATUS_OPTIONS.map((option) => (
								<MenuItem key={option.value || 'all'} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						label="Conversation ID"
						variant="outlined"
						size="small"
						value={filters.conversationId}
						onChange={(event) => setFilters((prev) => ({ ...prev, conversationId: event.target.value }))}
						className={styles.filterField}
					/>
					<Box className={styles.filterActions}>
						<Button variant="contained" onClick={handleSearch} disabled={isLoading}>
							Search
						</Button>
						<Button variant="outlined" onClick={handleReset} disabled={isLoading}>
							Reset
						</Button>
					</Box>
				</Box>

				<Box className={styles.metaRow}>
					<Typography variant="body2">
						{totalCount ? `Total conversations: ${totalCount}` : 'No conversations found.'}
					</Typography>
					{errorMessage && (
						<Typography variant="body2" color="error">
							{errorMessage}
						</Typography>
					)}
				</Box>

				<MaterialReactTable
					columns={columns}
					data={isLoading ? [] : conversations}
					enableColumnOrdering
					enableGlobalFilter
					enableColumnFilters={false}
					enableRowSelection={false}
					enableStickyHeader
					manualPagination
					rowCount={totalCount}
					enablePagination
					muiTablePaginationProps={{
						rowsPerPageOptions: [10],
					}}
					onPaginationChange={(updater) => {
						const next = typeof updater === 'function'
							? updater({ pageIndex: page - 1, pageSize: DEFAULT_PAGE_SIZE })
							: updater;
						const nextPage = next.pageIndex + 1;
						setPage(nextPage);
						setSearchParams({
							...appliedFilters,
							page: String(nextPage),
						});
					}}
					state={{
						isLoading,
						pagination: { pageIndex: page - 1, pageSize: DEFAULT_PAGE_SIZE },
					}}
					initialState={{ density: 'compact', pagination: { pageSize: DEFAULT_PAGE_SIZE } }}
					muiTablePaperProps={{ elevation: 0 }}
					muiTableContainerProps={{ className: styles.tableWrapper }}
				/>
			</Paper>
		</div>
	);
};

export default IntercomConversations;
